import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import styles from "./styles.module.css";
import { z } from "zod";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Step,
  StepButton,
  StepLabel,
  Stepper,
  Switch,
  TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ContactsIcon from "@mui/icons-material/Contacts";
import BusinessIcon from "@mui/icons-material/Business";
import GenericTextField from "src/components/GenericTextField";
import GenericDatePicker from "src/components/GenericDatePicker";
import GenericAutoCompleteSelect from "src/components/GenericAutoCompleteSelect";
import StepAtiva from "src/components/StepAtiva";
import StepInativa from "src/components/StepInativa";
import GenericNumberTextField from "src/components/GenericNumberTextField";
import GenericCodIbgeInputs from "src/components/GenericCodIbgeInputs";
import ReactInputMask from "react-input-mask";
import { consultaCep } from "src/services/viaCep.service";
import StepCustomizada from "src/components/StepCustomizada";
import { Close, Domain, UploadFile } from "@mui/icons-material";
import { cadastraGrupoEconomico } from "src/services/api-empresas.service";

const DadosGeraisClienteFormSchema = z
  .object({
    idEmpresa: z.number().readonly(),
    cnpj: z
      .string({
        required_error: "CNPJ é obrigatório!",
      })
      .min(1, {
        message: "CNPJ é obrigatório!",
      })
      .refine(
        (value) => {
          const digitsOnly = value.replace(/\D/g, "");
          return digitsOnly.length === 11 || digitsOnly.length === 14;
        },
        {
          message: "CNPJ (ou CPF) deve conter 11 ou 14 dígitos.",
        }
      ),
    razaoSocial: z
      .string({
        required_error: "Razão social é obrigatória!",
      })
      .min(1),
    dtAbertura: z.date({
      required_error: "Data de abertura é obrigatório!",
    }),
    dtBaixa: z.date(),
    inscricaoMunicipal: z.string(),
    inscricaoEstadual: z
      .string({
        required_error: "Inscrição estadual é obrigatório!",
      })
      .min(1),
    ativo: z.boolean(),
    controlador: z.boolean(),
    idUsuarioParceiro: z.number({
      required_error: "Parceiro é obrigatório!",
      invalid_type_error: "Parceiro é obrigatório!",
    }),
    servicos: z.array(
      z
        .object({
          idServico: z.number(),
          descricao: z.string(),
        })
        .required()
    ),
    certificadoFileName: z.string().readonly(),
    certificadoFile: z.union([
      z
        .instanceof(File)
        .refine(
          (file) => (file?.type || "") === "application/x-pkcs12",
          "Only .pfx files are supported"
        ),
      z.null(),
    ]),
  })
  .partial({
    idEmpresa: true,
    dtBaixa: true,
    inscricaoMunicipal: true,
    servicos: true,
    certificadoFileName: true,
    certificadoFile: true,
  })
  .required({
    cnpj: true,
    razaoSocial: true,
    idUsuarioParceiro: true,
    dtAbertura: true,
    inscricaoEstadual: true,
  });

const ContatosClienteFormSchema = z
  .array(
    z
      .object({
        descricao: z.string(),
        email: z
          .string({
            required_error: "Email é obrigatório!",
            invalid_type_error: "Email é obrigatório!",
          })
          .min(1, { message: "Email é obrigatório!" }),
        celular: z
          .string({
            required_error: "Celular é obrigatório!",
            invalid_type_error: "Celular é obrigatório!",
          })
          .min(1, { message: "Email é obrigatório!" }),
        telefone: z.string(),
      })
      .partial({ descricao: true, telefone: true })
      .required({ email: true, celular: true })
  )
  .nonempty();

const EnderecoClienteFormSchema = z
  .object({
    logradouro: z
      .string({
        required_error: "Logradouro é obrigatório!",
        invalid_type_error: "Logradouro é obrigatório!",
      })
      .min(1, { message: "Logradouro é obrigatório!" }).transform((val) => val.replace("'", "")),
    numero: z
      .string({
        required_error: "Obrigatório!",
        invalid_type_error: "Obrigatório!",
      })
      .min(1, { message: "Obrigatório!" }),
    complemento: z.string().transform((val) => val.replace("'", "")),
    cep: z.string().transform((val) => val.replace(/[\-]/g, "")),
    bairro: z
      .string({
        required_error: "Bairro é obrigatório!",
        invalid_type_error: "Bairro é obrigatório!",
      })
      .min(1, { message: "Bairro é obrigatório!" }).transform((val) => val.replace("'", "")),
    codIbge: z.number({
      required_error: "Cidade é obrigatório!",
    }),
  })
  .partial({
    complemento: true,
  })
  .required({
    logradouro: true,
    numero: true,
    cep: true,
    bairro: true,
    codIbge: true,
  });

const GrupoEconomicoClienteFormSchema = z
  .array(
    z
      .object({
        idGrupoEconomico: z.number({
          required_error: "Escolha um grupo econômico!",
          invalid_type_error: "Escolha um grupo econômico!!",
        }),
        inicioVinculo: z.date(),
        fimVinculo: z.date(),
      })
      .partial({
        fimVinculo: true,
      })
      .required({
        idGrupoEconomico: true,
        inicioVinculo: true,
      })
  )
  .nonempty();

const ClienteFormSchema = z.object({
  empresa: DadosGeraisClienteFormSchema,
  contatos: ContatosClienteFormSchema,
  endereco: EnderecoClienteFormSchema,
  gruposEconomicos: GrupoEconomicoClienteFormSchema,
});

type ClienteFormData = z.infer<typeof ClienteFormSchema>;

type ClienteFormProps = {
  clienteValues: ClienteFormData;
  onSubmit(data: ClienteFormData): void;
  onClose(event: any): void;
};

export default function ClienteForm({
  clienteValues,
  onSubmit,
  onClose,
}: ClienteFormProps) {
  const defaultValues = {
    empresa: {
      idEmpresa: clienteValues.empresa.idEmpresa || undefined,
      cnpj: clienteValues?.empresa?.cnpj,
      razaoSocial: clienteValues?.empresa?.razaoSocial,
      dtAbertura: !!clienteValues.empresa.dtAbertura
        ? new Date(clienteValues?.empresa?.dtAbertura)
        : undefined,
      dtBaixa: !!clienteValues.empresa.dtBaixa
        ? new Date(clienteValues?.empresa?.dtBaixa)
        : undefined,
      inscricaoMunicipal: clienteValues?.empresa?.inscricaoMunicipal,
      inscricaoEstadual: clienteValues?.empresa?.inscricaoEstadual,
      ativo: clienteValues?.empresa?.ativo,
      controlador: clienteValues?.empresa?.controlador,
      idUsuarioParceiro: clienteValues?.empresa?.idUsuarioParceiro,
      servicos: clienteValues.empresa.servicos || [],
      certificadoFileName: clienteValues?.empresa?.certificadoFileName || "",
      certificadoFile: null,
    },
    contatos: !!clienteValues.contatos
      ? clienteValues.contatos.map((contato) => {
          return {
            descricao: contato?.descricao,
            email: contato?.email,
            celular: contato?.celular,
            telefone: contato?.telefone,
          };
        })
      : [getNewContato()],
    endereco: {
      cep: clienteValues?.endereco?.cep || undefined,
      logradouro: clienteValues?.endereco?.logradouro || undefined,
      numero: clienteValues?.endereco?.numero || undefined,
      complemento: clienteValues?.endereco?.complemento || undefined,
      bairro: clienteValues?.endereco?.bairro || undefined,
      codIbge: clienteValues?.endereco?.codIbge || undefined,
    },
    gruposEconomicos: !!clienteValues.gruposEconomicos
      ? clienteValues.gruposEconomicos.map((grupoEconomico) => {
          return {
            idGrupoEconomico: grupoEconomico?.idGrupoEconomico,
            inicioVinculo: new Date(grupoEconomico?.inicioVinculo),
            fimVinculo: grupoEconomico?.fimVinculo
              ? new Date(grupoEconomico?.fimVinculo)
              : grupoEconomico?.fimVinculo,
          };
        })
      : [getNewGrupoEconomico()],
  };

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    resolver: zodResolver(
      z.object({
        empresa: DadosGeraisClienteFormSchema,
        contatos: ContatosClienteFormSchema,
        endereco: EnderecoClienteFormSchema,
        gruposEconomicos: GrupoEconomicoClienteFormSchema,
      })
    ),
    defaultValues,
  });

  const [openNewGrupoEconomicoModal, setOpenNewGrupoEconomicoModal] =
    useState(false);
  const [editRowData, setEditRowData] = useState<any>({});
  const [grupoEconomicoTrigger, setGrupoEconomicoTrigger] = useState(0);
  const [newGrupoEconomicoModalError, setNewGrupoEconomicoModalError] =
    useState(false);
  const [
    newGrupoEconomicoModalHelperText,
    setNewGrupoEconomicoModalHelperText,
  ] = useState("");
  const certFileInputRef = useRef<HTMLInputElement>(null);

  const watchCep: any = watch("endereco.cep");

  useEffect(() => {
    if (watchCep && watchCep?.length == 9) {
      (async () => {
        try {
          const buscaCep = await consultaCep(watchCep.replace(/\'-/g, ""));
          setValue("endereco.bairro", buscaCep.data.bairro || "");
          setValue("endereco.logradouro", buscaCep.data.logradouro || "");
          setValue(
            "endereco.codIbge",
            parseInt(buscaCep.data.ibge) || undefined
          );
          setUf(buscaCep.data.uf);

          setIbgeCod(parseInt(buscaCep.data.ibge));

          trigger("endereco.bairro");
          trigger("endereco.logradouro");
          trigger("endereco.codIbge");
        } catch (err) {
          console.error(err);
        }
      })();
    }
  }, [watchCep]);

  const {
    fields: contatos,
    append: addContato,
    remove: removeContato,
  } = useFieldArray({
    control,
    name: "contatos",
  });

  const {
    fields: gruposEconomicos,
    append: addGruposEconomicos,
    remove: removeGruposEconomicos,
  } = useFieldArray({
    control,
    name: "gruposEconomicos",
  });

  const [uf, setUf] = useState("");

  const steps = ["Dados Gerais", "Contatos", "Endereço", "Grupo Ecônomico"];
  //#region useStates
  const [activeStep, setActiveStep] = useState(0);
  const [skipped, setSkipped] = useState(new Set<number>());
  const [ibgeCode, setIbgeCod] = useState<number>();
  const [certificateHelper, setCertificateHelper] = useState("");

  // console.log(ibgeCode);

  //#endregion

  const isStepSkipped = (step: number) => {
    return skipped.has(step);
  };

  const handleStep = (step: number) => () => {
    setActiveStep(step);
  };

  const advanceStep = () => {
    switch (activeStep) {
      case 0:
        if (
          !DadosGeraisClienteFormSchema.safeParse(getValues("empresa")).success
        )
          return;
        break;
      case 1:
        if (!ContatosClienteFormSchema.safeParse(getValues("contatos")).success)
          return;
        break;

      case 2:
        if (!EnderecoClienteFormSchema.safeParse(getValues("endereco")).success)
          return;
        break;

      case 3:
        if (
          !GrupoEconomicoClienteFormSchema.safeParse(
            getValues("gruposEconomicos")
          ).success
        )
          return;
        break;

      default:
        break;
    }
    setActiveStep(activeStep + 1);
  };

  const backtrackStep = () => {
    setActiveStep(activeStep - 1);
  };

  const handleCloseForm = (event: any) => {
    onClose(event);
  };

  function getNewContato() {
    return {
      descricao: undefined,
      email: undefined,
      telefone: undefined,
      celular: undefined,
    };
  }

  function getNewGrupoEconomico() {
    return {
      idGrupoEconomico: undefined,
      inicioVinculo: undefined,
      fimVinculo: undefined,
    };
  }

  function handleAddContato() {
    addContato(getNewContato());
  }

  function handleDeleteContato(index: number) {
    console.log(index);
    removeContato(index);
  }

  function handleAddGrupoEconomico() {
    addGruposEconomicos(getNewGrupoEconomico());
  }

  function handleOpenGrupoEconomico() {
    setEditRowData({});
    setOpenNewGrupoEconomicoModal(true);
  }

  function handleCloseNewGrupoEconomicoModal() {
    setOpenNewGrupoEconomicoModal(false);
  }

  async function handleSaveNewGrupoEconomicoModal() {
    const headers = {
      headers: {
        "Content-Type": "application/json",
        Authorization: localStorage.getItem("token"),
      },
    };

    const body = {
      grupoEconomico: {
        nome: editRowData.nome,
        ativo: editRowData.ativo,
      },
    };

    try {
      await cadastraGrupoEconomico(body, headers);
      setGrupoEconomicoTrigger((prev) => prev + 1);
      handleCloseNewGrupoEconomicoModal();
      setNewGrupoEconomicoModalError(false);
      setNewGrupoEconomicoModalHelperText("");
    } catch (err: any) {
      console.log(err);
      setNewGrupoEconomicoModalError(true);
      setNewGrupoEconomicoModalHelperText(
        "Houve um erro, tente novamente mais tarde!"
      );
    }
  }

  function handleDeleteGrupoEconomico(index: number) {
    removeGruposEconomicos(index);
  }

  const submit = async (data: ClienteFormData) => {
    if (activeStep === steps?.length - 1) onSubmit(data);
  };

  const getCepField = () => {
    return (
      <GenericTextField
        label="CEP"
        required
        error={Boolean(errors.endereco?.cep)}
        helperText={errors.endereco?.cep?.message}
        placeholder={"CEP"}
      />
    );
  };

  const getCNPJField = () => {
    return (
      <GenericTextField
        label="CNPJ"
        required
        error={Boolean(errors.empresa?.cnpj)}
        helperText={errors.empresa?.cnpj?.message}
        placeholder={"CNPJ"}
      />
    );
  };

  const handleCertificateFileClose = (e: any) => {
    setValue("empresa.certificadoFile", null);
    setValue("empresa.certificadoFileName", '');
  }

  const handleCertificateFileChange = (e: any) => {
    setCertificateHelper("");
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (file.name.endsWith(".pfx")) {
        setValue("empresa.certificadoFileName", file.name);
        setValue("empresa.certificadoFile", file);
      } else {
        setCertificateHelper("Arquivo deve ser .pfx");
        setValue("empresa.certificadoFileName", "");
        setValue("empresa.certificadoFile", null);
      }
    }
    trigger("empresa.certificadoFile");
    trigger("empresa.certificadoFileName");
  };

  const getStepForm = (label: string, index: number) => {
    const stepProps: { completed?: boolean } = {};

    if (isStepSkipped(index)) {
      stepProps.completed = false;
    }

    return (
      <Step key={label} {...stepProps}>
        {!clienteValues?.empresa.idEmpresa ? (
          <StepLabel
            StepIconComponent={(props) => (
              <StepCustomizada
                label={steps.indexOf(label) + 1}
                active={props.active}
              ></StepCustomizada>
            )}
          >
            {label}
          </StepLabel>
        ) : (
          <StepButton
            icon={
              activeStep === index ? (
                <StepInativa label={steps.indexOf(label) + 1}></StepInativa>
              ) : (
                <StepAtiva label={steps.indexOf(label) + 1}></StepAtiva>
              )
            }
            onClick={handleStep(index)}
          >
            {label}
          </StepButton>
        )}
      </Step>
    );
  };

  function getActiveStep(activeStep: number) {
    switch (activeStep) {
      case 0:
        return getDadosGeraisForm();
      case 1:
        return getContatosForm();
      case 2:
        return getEnderecoForm();
      case 3:
        return getGrupoEconomicoForm();
      default:
        return <></>;
    }
  }

  const getDadosGeraisForm = () => {
    return (
      <>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Box className={styles.idRow}>
              <Controller
                name="empresa.idEmpresa"
                control={control}
                defaultValue={clienteValues.empresa.idEmpresa || undefined}
                render={({ field: { ref, ...field } }) => (
                  <GenericNumberTextField
                    disabled
                    label="ID"
                    error={Boolean(errors.empresa?.idEmpresa)}
                    helperText={errors.empresa?.idEmpresa?.message}
                    placeholder={"Nova Empresa"}
                    inputRef={ref}
                    {...field}
                  />
                )}
              />
              <Controller
                name="empresa.ativo"
                control={control}
                defaultValue={clienteValues?.empresa?.ativo || false}
                render={({ field: { ref, ...field } }) => (
                  <FormControlLabel
                    control={<Switch color="primary" />}
                    label="Ativo?"
                    labelPlacement="start"
                    checked={field.value}
                    sx={{
                      margin: 0,
                    }}
                    inputRef={ref}
                    {...field}
                  />
                )}
              />
              <Controller
                name="empresa.controlador"
                control={control}
                defaultValue={clienteValues?.empresa?.controlador || false}
                render={({ field: { ref, ...field } }) => (
                  <FormControlLabel
                    control={<Switch color="primary" />}
                    label="Controlador?"
                    labelPlacement="start"
                    checked={field.value}
                    sx={{
                      margin: 0,
                    }}
                    inputRef={ref}
                    {...field}
                  />
                )}
              />
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Controller
              name="empresa.certificadoFileName"
              control={control}
              defaultValue={clienteValues?.empresa?.certificadoFileName || ""}
              render={({ field: { ref, onChange, ...field } }) => (
                <>
                  <TextField
                    {...field}
                    label="Certificado"
                    size={"small"}
                    variant={"outlined"}
                    placeholder={"Nenhum certificado escolhido"}
                    fullWidth
                    helperText={certificateHelper}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <>
                          {getValues("empresa.certificadoFile") !== null && (
                            <InputAdornment position="end">
                              <IconButton onClick={handleCertificateFileClose}>
                                <Close />
                              </IconButton>
                            </InputAdornment>
                          )}
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => {
                                  certFileInputRef?.current?.click();
                              }}
                            >
                              <UploadFile />
                            </IconButton>
                          </InputAdornment>
                        </>
                      ),
                    }}
                  />
                  <input
                    ref={certFileInputRef}
                    type="file"
                    value={''}
                    style={{ display: "none" }}
                    onChange={handleCertificateFileChange}
                  />
                </>
              )}
            />
          </Grid>
          <Grid item xs={4}>
            <Controller
              name="empresa.idUsuarioParceiro"
              control={control}
              defaultValue={
                clienteValues?.empresa?.idUsuarioParceiro || undefined
              }
              render={({ field: { ref, ...field } }) => (
                <GenericAutoCompleteSelect
                  codigoCombo={"PARCEIRO"}
                  label={"Parceiro"}
                  replaceId={"idUsuarioParceiro"}
                  replaceLabel={"descricao"}
                  placeholder={"Pesquise parceiros..."}
                  error={Boolean(errors.empresa?.idUsuarioParceiro)}
                  helperText={errors.empresa?.idUsuarioParceiro?.message}
                  required
                  inputRef={ref}
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid item xs={4}>
            <Controller
              name="empresa.cnpj"
              control={control}
              defaultValue={clienteValues?.empresa?.cnpj || undefined}
              render={({ field: { ref, ...field } }) => (
                <ReactInputMask
                  mask="99.999.999/9999-99"
                  maskChar={null}
                  {...field}
                >
                  {getCNPJField}
                </ReactInputMask>
              )}
            />
          </Grid>
          <Grid item xs={8}>
            <Controller
              name="empresa.razaoSocial"
              control={control}
              defaultValue={clienteValues?.empresa?.razaoSocial || undefined}
              render={({ field: { ref, ...field } }) => (
                <GenericTextField
                  label="Razão Social"
                  required
                  error={Boolean(errors.empresa?.razaoSocial)}
                  helperText={errors.empresa?.razaoSocial?.message}
                  placeholder={"Razão Social"}
                  inputRef={ref}
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid item xs={4}>
            <Controller
              name="empresa.inscricaoEstadual"
              control={control}
              defaultValue={
                clienteValues?.empresa?.inscricaoEstadual || undefined
              }
              render={({ field: { ref, ...field } }) => (
                <GenericTextField
                  label="Inscrição Estadual"
                  error={Boolean(errors.empresa?.inscricaoEstadual)}
                  helperText={errors.empresa?.inscricaoEstadual?.message}
                  placeholder={"Inscrição Estadual"}
                  required
                  inputRef={ref}
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid item xs={4}>
            <Controller
              name="empresa.inscricaoMunicipal"
              control={control}
              defaultValue={
                clienteValues?.empresa?.inscricaoMunicipal || undefined
              }
              render={({ field: { ref, ...field } }) => (
                <GenericTextField
                  label="Inscrição Municipal"
                  error={Boolean(errors.empresa?.inscricaoMunicipal)}
                  helperText={errors.empresa?.inscricaoMunicipal?.message}
                  placeholder={"Inscrição Municipal"}
                  inputRef={ref}
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid item xs={2}>
            <Controller
              name="empresa.dtAbertura"
              control={control}
              defaultValue={clienteValues?.empresa?.dtAbertura || undefined}
              render={({ field: { ref, ...field } }) => (
                <GenericDatePicker
                  label="Data de Abertura"
                  placeholder="Data de Abertura"
                  inputRef={ref}
                  error={Boolean(errors.empresa?.dtAbertura)}
                  helperText={errors.empresa?.dtAbertura?.message}
                  required
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid item xs={2}>
            <Controller
              name="empresa.dtBaixa"
              control={control}
              defaultValue={clienteValues?.empresa?.dtBaixa || undefined}
              render={({ field: { ref, ...field } }) => (
                <GenericDatePicker
                  label="Data de Baixa"
                  placeholder="Data de Baixa"
                  inputRef={ref}
                  error={Boolean(errors.empresa?.dtBaixa)}
                  helperText={errors.empresa?.dtBaixa?.message}
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="empresa.servicos"
              control={control}
              defaultValue={clienteValues?.empresa?.servicos || undefined}
              render={({ field: { ref, ...field } }) => (
                <GenericAutoCompleteSelect
                  codigoCombo={"SERVICO"}
                  label={"Serviços"}
                  replaceId={"idServico"}
                  replaceLabel={"descricao"}
                  placeholder={"Pesquise serviços..."}
                  error={Boolean(errors.empresa?.servicos)}
                  helperText={errors.empresa?.servicos?.message}
                  required
                  multiple
                  preSelectAll={clienteValues?.empresa?.idEmpresa === null}
                  inputRef={ref}
                  {...field}
                />
              )}
            />
          </Grid>
        </Grid>
      </>
    );
  };

  const getContatosForm = () => {
    return (
      <>
        {contatos.map((field, index) => {
          return (
            <Box
              key={`contato-${index}`}
              sx={index > 0 ? { paddingTop: "18px" } : { marginTop: "-18px" }}
            >
              <Grid container spacing={2}>
                <Grid item xs={11}>
                  <Box sx={{ paddingTop: "6px" }}>
                    <Divider
                      variant="middle"
                      textAlign="left"
                      sx={{
                        "&::before, &::after": {
                          borderColor: "#0f2047",
                        },
                      }}
                    >
                      <Box sx={{ paddingRight: "18px" }}>
                        <Button disabled>
                          <ContactsIcon sx={{ color: "#0f2047" }} />
                        </Button>
                        {`Contato ${index + 1}`}
                      </Box>
                    </Divider>
                  </Box>
                </Grid>
                <Grid item xs={1}>
                  {contatos.length > 1 && (
                    <Button>
                      <DeleteIcon
                        sx={{ color: "#0f2047" }}
                        onClick={() => {
                          handleDeleteContato(index);
                        }}
                      />
                    </Button>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name={`contatos.${index}.descricao`}
                    control={control}
                    defaultValue={field.descricao || ""}
                    render={({ field: { ref, ...field } }) => (
                      <GenericTextField
                        label="Descrição"
                        error={Boolean(errors.contatos?.[index]?.descricao)}
                        helperText={
                          errors.contatos?.[index]?.descricao?.message
                        }
                        placeholder={"Descrição"}
                        inputRef={ref}
                        {...field}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name={`contatos.${index}.email`}
                    control={control}
                    defaultValue={field.email || ""}
                    render={({ field: { ref, ...field } }) => (
                      <GenericTextField
                        label="E-mail"
                        required
                        error={Boolean(errors.contatos?.[index]?.email)}
                        helperText={errors.contatos?.[index]?.email?.message}
                        placeholder={"E-mail"}
                        inputRef={ref}
                        {...field}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name={`contatos.${index}.telefone`}
                    control={control}
                    defaultValue={field.telefone || ""}
                    render={({ field: { ref, ...field } }) => (
                      <GenericTextField
                        label="Telefone"
                        error={Boolean(errors.contatos?.[index]?.telefone)}
                        helperText={errors.contatos?.[index]?.telefone?.message}
                        placeholder={"Telefone"}
                        inputRef={ref}
                        {...field}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name={`contatos.${index}.celular`}
                    control={control}
                    defaultValue={field.celular || ""}
                    render={({ field: { ref, ...field } }) => (
                      <GenericTextField
                        label="Celular"
                        required
                        error={Boolean(errors.contatos?.[index]?.celular)}
                        helperText={errors.contatos?.[index]?.celular?.message}
                        placeholder={"Celular"}
                        inputRef={ref}
                        {...field}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          );
        })}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ paddingTop: "18px" }}>
              <Divider
                variant="middle"
                textAlign="left"
                sx={{
                  "&::before, &::after": {
                    borderColor: "#0f2047",
                  },
                }}
              >
                <Box sx={{ paddingRight: "18px" }}>
                  <Button>
                    <AddCircleIcon
                      sx={{ color: "#0f2047" }}
                      onClick={() => {
                        handleAddContato();
                      }}
                    />
                  </Button>
                  Adicionar Novo Contato
                </Box>
              </Divider>
            </Box>
          </Grid>
        </Grid>
      </>
    );
  };

  const getEnderecoForm = () => {
    return (
      <>
        <Grid container spacing={2}>
          <Grid item xs={2}>
            <Controller
              name="endereco.cep"
              control={control}
              defaultValue={clienteValues?.endereco?.cep || undefined}
              render={({ field: { ref, ...field } }) => (
                <ReactInputMask mask="99999-999" maskChar={null} {...field}>
                  {getCepField}
                </ReactInputMask>
              )}
            />
          </Grid>
          <Grid item xs={10}></Grid>
          <Grid item xs={9}>
            <Controller
              name="endereco.logradouro"
              control={control}
              defaultValue={clienteValues?.endereco?.logradouro || undefined}
              render={({ field: { ref, ...field } }) => (
                <GenericTextField
                  label="Logradouro"
                  required
                  error={Boolean(errors.endereco?.logradouro)}
                  helperText={errors.endereco?.logradouro?.message}
                  placeholder={"Logradouro"}
                  inputRef={ref}
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid item xs={1}>
            <Controller
              name="endereco.numero"
              control={control}
              defaultValue={clienteValues?.endereco?.numero || undefined}
              render={({ field: { ref, ...field } }) => (
                <GenericTextField
                  label="Nº"
                  required
                  error={Boolean(errors.endereco?.numero)}
                  helperText={errors.endereco?.numero?.message}
                  placeholder={"Nº"}
                  inputRef={ref}
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid item xs={2}>
            <Controller
              name="endereco.complemento"
              control={control}
              defaultValue={clienteValues?.endereco?.complemento || undefined}
              render={({ field: { ref, ...field } }) => (
                <GenericTextField
                  label="Complemento"
                  error={Boolean(errors.endereco?.complemento)}
                  helperText={errors.endereco?.complemento?.message}
                  placeholder={"Complemento"}
                  inputRef={ref}
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="endereco.bairro"
              control={control}
              defaultValue={clienteValues?.endereco?.bairro || undefined}
              render={({ field: { ref, ...field } }) => (
                <GenericTextField
                  label="Bairro"
                  required
                  error={Boolean(errors.endereco?.bairro)}
                  helperText={errors.endereco?.bairro?.message}
                  placeholder={"Bairro"}
                  inputRef={ref}
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="endereco.codIbge"
              control={control}
              defaultValue={clienteValues?.endereco?.codIbge || undefined}
              render={({ field: { ref, onChange, ...field } }) => (
                <GenericCodIbgeInputs
                  cityValue={ibgeCode}
                  onChange={onChange}
                  error={Boolean(errors.endereco?.codIbge)}
                  helperText={errors.endereco?.codIbge?.message}
                  uf={uf}
                  inputRef={ref}
                  {...field}
                />
              )}
            />
          </Grid>
        </Grid>
      </>
    );
  };

  const getGrupoEconomicoForm = () => {
    return (
      <>
        {gruposEconomicos.map((field, index) => {
          return (
            <Box
              key={`grupo-economico-${index}`}
              sx={index > 0 ? { paddingTop: "18px" } : { marginTop: "-18px" }}
            >
              <Grid container spacing={2}>
                <Grid item xs={11}>
                  <Box sx={{ paddingTop: "6px" }}>
                    <Divider
                      variant="middle"
                      textAlign="left"
                      sx={{
                        "&::before, &::after": {
                          borderColor: "#0f2047",
                        },
                      }}
                    >
                      <Box sx={{ paddingRight: "18px" }}>
                        <Button disabled>
                          <BusinessIcon sx={{ color: "#0f2047" }} />
                        </Button>
                        {`Grupo Econômico ${index + 1}`}
                      </Box>
                    </Divider>
                  </Box>
                </Grid>
                <Grid item xs={1}>
                  {gruposEconomicos.length > 1 && (
                    <Button>
                      <DeleteIcon
                        sx={{ color: "#0f2047" }}
                        onClick={() => {
                          handleDeleteGrupoEconomico(index);
                        }}
                      />
                    </Button>
                  )}
                </Grid>
                <Grid item xs={8}>
                  <Controller
                    name={`gruposEconomicos.${index}.idGrupoEconomico`}
                    control={control}
                    defaultValue={field.idGrupoEconomico || undefined}
                    render={({ field: { ref, ...field } }) => (
                      <GenericAutoCompleteSelect
                        key={grupoEconomicoTrigger}
                        codigoCombo={"GRUPOECONOMICO"}
                        label={"Grupo Econômico"}
                        replaceId={"idGrupoEconomico"}
                        replaceLabel={"descricao"}
                        placeholder={"Pesquise grupos econômicos..."}
                        error={Boolean(
                          errors.gruposEconomicos?.[index]?.idGrupoEconomico
                        )}
                        helperText={
                          errors.gruposEconomicos?.[index]?.idGrupoEconomico
                            ?.message
                        }
                        required
                        inputRef={ref}
                        {...field}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={2}>
                  <Controller
                    name={`gruposEconomicos.${index}.inicioVinculo`}
                    control={control}
                    defaultValue={field.inicioVinculo || undefined}
                    render={({ field: { ref, ...field } }) => (
                      <GenericDatePicker
                        label="Inicio Vínculo"
                        placeholder="Inicio Vínculo"
                        inputRef={ref}
                        error={Boolean(
                          errors.gruposEconomicos?.[index]?.inicioVinculo
                        )}
                        helperText={
                          errors.gruposEconomicos?.[index]?.inicioVinculo
                            ?.message
                        }
                        required
                        {...field}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={2}>
                  <Controller
                    name={`gruposEconomicos.${index}.fimVinculo`}
                    control={control}
                    defaultValue={field.fimVinculo || undefined}
                    render={({ field: { ref, ...field } }) => (
                      <GenericDatePicker
                        label="Fim Vínculo"
                        placeholder="Fim Vínculo"
                        inputRef={ref}
                        error={Boolean(
                          errors.gruposEconomicos?.[index]?.fimVinculo
                        )}
                        helperText={
                          errors.gruposEconomicos?.[index]?.fimVinculo?.message
                        }
                        {...field}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          );
        })}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ paddingTop: "18px" }}>
              <Divider
                variant="middle"
                textAlign="left"
                sx={{
                  "&::before, &::after": {
                    borderColor: "#0f2047",
                  },
                }}
              >
                <Box sx={{ paddingRight: "18px" }}>
                  <Button
                    sx={{
                      textTransform: "none !important",
                      fontWeight: "normal !important",
                      fontSize: "inherit !important",
                      color: "inherit !important",
                    }}
                    onClick={() => {
                      handleAddGrupoEconomico();
                    }}
                  >
                    <AddCircleIcon
                      sx={{ color: "#0f2047", marginRight: "8px" }}
                    />
                    Adicionar Novo Vínculo de Grupo Econômico
                  </Button>
                </Box>
              </Divider>

              <Divider
                variant="middle"
                textAlign="left"
                sx={{
                  "&::before, &::after": {
                    borderColor: "#0f2047",
                  },
                }}
              >
                <Box sx={{ paddingRight: "18px" }}>
                  <Button
                    sx={{
                      textTransform: "none !important",
                      fontWeight: "normal !important",
                      fontSize: "inherit !important",
                      color: "inherit !important",
                    }}
                    onClick={() => {
                      handleOpenGrupoEconomico();
                    }}
                  >
                    <Domain sx={{ color: "#0f2047", marginRight: "8px" }} />
                    <AddCircleIcon
                      sx={{ color: "#0f2047", marginRight: "8px" }}
                    />
                    Cadastrar Novo Grupo Econômico
                  </Button>
                </Box>
              </Divider>
            </Box>
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <>
      <form onSubmit={handleSubmit(submit)} className={styles.formDiv}>
        <Stepper nonLinear activeStep={activeStep}>
          {steps.map((label, index) => getStepForm(label, index))}
        </Stepper>
        <DialogContent sx={{ padding: "8px" }}>
          <Box className={styles.formContainer}>
            {getActiveStep(activeStep)}
          </Box>
        </DialogContent>
        <DialogActions className={styles.botoesPopupGeral}>
          <Button
            className={styles.botaoPopupCancelar}
            onClick={handleCloseForm}
            size="small"
          >
            Cancelar
          </Button>
          <div style={{ flex: "1 0 0" }} />
          {activeStep > 0 && (
            <Button
              className={styles.botaoPopupCancelar}
              color="primary"
              size="small"
              onClick={backtrackStep}
            >
              Voltar
            </Button>
          )}
          {activeStep < steps.length - 1 && (
            <Button
              className={styles.botaoPopupCancelar}
              color="primary"
              type="submit"
              size="small"
              onClick={() => {
                console.log(errors);
                advanceStep();
              }}
            >
              Próximo
            </Button>
          )}
          {activeStep === steps?.length - 1 && (
            <Button
              className={styles.botaoPopupSalvar}
              type="submit"
              color="primary"
              size="small"
              onClick={() => console.log(errors)}
            >
              Salvar
            </Button>
          )}
        </DialogActions>
      </form>

      <Dialog
        open={openNewGrupoEconomicoModal}
        onClose={handleCloseNewGrupoEconomicoModal}
        PaperProps={{
          style: {
            width: "740px", // Defina a largura desejada
          },
        }}
        maxWidth="xl" // Defina o tamanho máximo do modal
      >
        <DialogTitle sx={{ paddingY: "0px" }}>
          <div className={styles.tituloPopupContainer}>
            <Domain sx={{ fontSize: "40px", marginRight: "8px" }} />
            <h1 className={styles.tituloPopup}>Cadastrar Grupo Econômico</h1>
          </div>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ marginTop: "4px" }}>
            <Grid item xs={10}>
              <TextField
                label={"Nome"}
                value={editRowData.nome}
                onChange={(e) =>
                  setEditRowData({ ...editRowData, nome: e.target.value })
                }
                error={newGrupoEconomicoModalError}
                helperText={newGrupoEconomicoModalHelperText}
                variant="outlined"
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={2}>
              <label>Ativo</label>
              <Switch
                checked={editRowData.ativo}
                onChange={(e) =>
                  setEditRowData({ ...editRowData, ativo: e.target.checked })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions className={styles.botoesPopupGeral}>
          <Button
            className={styles.botaoPopupCancelar}
            onClick={handleCloseNewGrupoEconomicoModal}
            size="small"
          >
            Cancelar
          </Button>
          <Button
            className={styles.botaoPopupSalvar}
            onClick={handleSaveNewGrupoEconomicoModal}
            color="primary"
            size="small"
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
